// Extracted from GuestDetail.jsx during the Feb 2026 component split.
// Contains PromoTab (default export) + all promo helpers (SectionHeader,
// splitSwipe/joinSwipe, PromoAssetCard, RegenConfirmDialog, PromoAssistantPanel,
// SwipeSplitterCards, parseGuestEmail, GuestEmailCard, SwipeOptionCard, ShareKit).
import React, { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import {
  Copy, Sparkles, Check, MessageCircle, RefreshCw, Send, Trash2, Upload, Wand2,
  Download, ExternalLink, Link2, Pencil, X, RotateCcw, History,
} from "lucide-react";
import { SHOW_FIELD_LABELS } from "./shared";

const HOST_PROMO_FIELDS = [
  { key: "episode_title", label: "Episode title" },
  { key: "short_description", label: "Short description — Apple / Spotify" },
  { key: "episode_description", label: "Long description" },
  { key: "social_post", label: "Your social post — LinkedIn / Facebook" },
  { key: "youtube_description", label: "YouTube SEO description" },
];

const GUEST_PROMO_FIELDS = [
  { key: "guest_swipe", label: "Guest social post — LinkedIn / Facebook" },
  { key: "guest_short_social", label: "Guest short post — X / Threads / Instagram / DM" },
];

const PROMO_FIELDS = [...HOST_PROMO_FIELDS, ...GUEST_PROMO_FIELDS];

function SectionHeader({ label, tint }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0 12px 0" }}>
      <div style={{ width: 4, height: 22, background: tint || "var(--pgm-magenta)" }} />
      <div className="pgm-micro" style={{ color: tint || "var(--pgm-magenta)", fontSize: 11 }}>◆ {label}</div>
      <div style={{ flex: 1, borderTop: "1.5px dashed var(--pgm-rule)" }} />
    </div>
  );
}

function splitSwipe(text) {
  const raw = (text || "").trim();
  if (!raw) return [];
  const blocks = raw.split(/\n{2,}/).map(b => b.trim()).filter(Boolean);
  if (blocks.length >= 2) return blocks.slice(0, 3);
  // fallback to single-newline separator when the model didn't leave blank lines
  const lines = raw.split(/\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length >= 3) return lines.slice(0, 3);
  return [raw];
}

function joinSwipe(segments) {
  return (segments || []).map(s => (s || "").trim()).filter(Boolean).join("\n\n");
}

function PromoTab({ g, onGenerated, onKitImages }) {
  const [loading, setLoading] = useState(false);
  const [regenKey, setRegenKey] = useState(null);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const promo = g.promo || {};
  const editedMap = g.promo_edited || {};
  const historyMap = g.promo_history || {};
  const hasAny = Object.keys(promo).some(k => promo[k]);
  const anyEdited = PROMO_FIELDS.some(f => editedMap[f.key]);

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  const generate = async (body) => {
    setError("");
    try {
      const { data } = await api.post(`/guests/${g.id}/promo`, body);
      const jobId = data.job_id;
      const started = Date.now();
      while (Date.now() - started < 240000) {
        await sleep(3000);
        const { data: s } = await api.get(`/guests/${g.id}/promo/status/${jobId}`);
        if (s.status === "done") { onGenerated(s); return; }
        if (s.status === "error") { setError(s.error || "Failed to generate promo copy"); return; }
      }
      setError("Generation is taking longer than expected — refresh the page in a minute.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to generate promo copy");
    }
  };

  const runPageRegen = async (mode) => {
    setConfirmOpen(false);
    setLoading(true);
    await generate({ mode });
    setLoading(false);
  };

  const onPageRegenClick = () => {
    if (hasAny && anyEdited) setConfirmOpen(true);
    else runPageRegen("all");
  };

  const regenOne = async (key) => {
    setRegenKey(key);
    await generate({ keys: [key] });
    setRegenKey(null);
  };

  return (
    <div className="pgm-reveal">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: "var(--pgm-wine)", opacity: 0.8, maxWidth: 600 }}>
          Every box below is yours to write in — type your own copy any time, or let Claude Sonnet draft it. The top section is for you (host), the bottom is what the guest can share. Generating pulls from intake bio, LinkedIn About, topics, and your show description.
        </p>
        <button className="pgm-btn" onClick={onPageRegenClick} disabled={loading || !!regenKey} data-testid="generate-promo-btn">
          <Sparkles size={14} /> {loading ? "Generating..." : (hasAny ? "Regenerate" : "Generate promo")}
        </button>
      </div>
      {error && <div className="pgm-callout" style={{ background: "var(--pgm-orange)", color: "var(--pgm-cream)", marginBottom: 14 }} data-testid="promo-error">{error}</div>}
      <div className="pgm-reveal-stagger" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <SectionHeader label="FOR YOU — HOST'S COPY" tint="var(--pgm-wine)" />
          {HOST_PROMO_FIELDS.map(({ key, label }) => (
            <PromoAssetCard
              key={key}
              guestId={g.id}
              assetKey={key}
              label={label}
              text={promo[key]}
              isEdited={!!editedMap[key]}
              history={historyMap[key] || []}
              regenBusy={regenKey === key}
              disabled={loading || (!!regenKey && regenKey !== key)}
              onRegen={() => regenOne(key)}
              onSaved={onGenerated}
            />
          ))}
          <SectionHeader label="FOR YOUR GUEST" tint="var(--pgm-magenta)" />
          {GUEST_PROMO_FIELDS.map(({ key, label }) => (
            <PromoAssetCard
              key={key}
              guestId={g.id}
              assetKey={key}
              label={label}
              text={promo[key]}
              isEdited={!!editedMap[key]}
              history={historyMap[key] || []}
              regenBusy={regenKey === key}
              disabled={loading || (!!regenKey && regenKey !== key)}
              onRegen={() => regenOne(key)}
              onSaved={onGenerated}
            />
          ))}
          <GuestEmailCard
              guestId={g.id}
              email={promo.guest_email}
              isEdited={!!editedMap["guest_email"]}
              history={historyMap["guest_email"] || []}
              regenBusy={regenKey === "guest_email"}
              disabled={loading || (!!regenKey && regenKey !== "guest_email")}
              onRegen={() => regenOne("guest_email")}
              onSaved={onGenerated}
              variant="guest"
            />
      </div>

      {confirmOpen && (
        <RegenConfirmDialog
          onAll={() => runPageRegen("all")}
          onUnedited={() => runPageRegen("unedited")}
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      <ShareKit g={g} onKitImages={onKitImages} />
    </div>
  );
}

function PromoHistoryPanel({ guestId, assetKey, entries, onRestored, onClose }) {
  const [busy, setBusy] = useState(-1);
  const [err, setErr] = useState("");

  const restore = async (i) => {
    setBusy(i); setErr("");
    try {
      const { data } = await api.post(`/guests/${guestId}/promo/${assetKey}/restore`, { index: i });
      onRestored(data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't restore that version");
    } finally { setBusy(-1); }
  };

  const when = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div style={{ marginTop: 12, border: "1.5px solid var(--pgm-olive)", background: "var(--pgm-cream)", padding: 12 }} data-testid={`history-panel-${assetKey}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--pgm-olive)" }}>
          Previous versions — newest first
        </div>
        <button className="pgm-btn pgm-btn-ghost" style={{ padding: "3px 10px", fontSize: 10 }} onClick={onClose} data-testid={`history-close-${assetKey}`}>
          <X size={10} /> Close
        </button>
      </div>
      {err && <div style={{ fontSize: 12, color: "var(--pgm-orange)", marginBottom: 8 }} data-testid={`history-error-${assetKey}`}>{err}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {entries.map((e, i) => (
          <div key={`${assetKey}-hist-${e.at || i}`} style={{ background: "#fff", border: "1px solid var(--pgm-rule)", padding: 10 }} data-testid={`history-item-${assetKey}-${i}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
              <div style={{ fontSize: 11, opacity: 0.7 }}>{when(e.at)}</div>
              <button
                className="pgm-btn pgm-btn-ghost"
                style={{ padding: "3px 10px", fontSize: 10 }}
                onClick={() => restore(i)}
                disabled={busy >= 0}
                data-testid={`history-restore-${assetKey}-${i}`}
              >
                <RotateCcw size={10} /> {busy === i ? "Restoring…" : "Restore"}
              </button>
            </div>
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial", fontSize: 12.5, lineHeight: 1.5, color: "var(--pgm-ink)", margin: 0, maxHeight: 120, overflow: "auto" }}>{e.text}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}

function PromoAssetCard({ guestId, assetKey, label, text, isEdited, history = [], regenBusy, disabled, onRegen, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const startEdit = () => { setDraft(text || ""); setSaveError(""); setEditing(true); };
  const save = async () => {
    setSaving(true); setSaveError("");
    try {
      const { data } = await api.put(`/guests/${guestId}/promo/${assetKey}`, { text: draft });
      onSaved(data);
      setEditing(false);
    } catch (e) {
      setSaveError(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };
  const copy = () => {
    navigator.clipboard.writeText(text || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const btnStyle = { padding: "6px 12px", fontSize: 11 };

  return (
    <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)" }} data-testid={`promo-${assetKey}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="pgm-micro">◆ {label}</div>
          {isEdited && (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", background: "var(--pgm-olive)", color: "var(--pgm-cream)", padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`edited-badge-${assetKey}`}>
              <Pencil size={9} /> Edited
            </span>
          )}
        </div>
        {!editing && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={copy} data-testid={`copy-${assetKey}`}>
              {copied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={startEdit} disabled={regenBusy || disabled} data-testid={`edit-${assetKey}`}>
              <Pencil size={12} /> Edit
            </button>
            <button
              className="pgm-btn pgm-btn-ghost"
              style={{ ...btnStyle, borderColor: chatOpen ? "var(--pgm-magenta)" : undefined, color: chatOpen ? "var(--pgm-magenta)" : undefined }}
              onClick={() => setChatOpen(v => !v)}
              disabled={regenBusy || disabled}
              data-testid={`ai-assist-${assetKey}`}
              title="Talk to AI about this asset"
            >
              <Wand2 size={12} /> {chatOpen ? "Close chat" : "AI Assistant"}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={onRegen} disabled={regenBusy || disabled} data-testid={`regen-${assetKey}`}>
              <RefreshCw size={12} /> {regenBusy ? "Generating..." : ((text || "").trim() ? "Regenerate" : "Generate")}
            </button>
            {history.length > 0 && (
              <button
                className="pgm-btn pgm-btn-ghost"
                style={{ ...btnStyle, borderColor: historyOpen ? "var(--pgm-olive)" : undefined, color: historyOpen ? "var(--pgm-olive)" : undefined }}
                onClick={() => setHistoryOpen(v => !v)}
                data-testid={`history-${assetKey}`}
                title="Restore an earlier version"
              >
                <History size={12} /> History ({history.length})
              </button>
            )}
          </div>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.min(18, Math.max(4, draft.split("\n").length + 1))}
            style={{ width: "100%", fontFamily: "Arial", fontSize: 13.5, lineHeight: 1.55, color: "var(--pgm-ink)", border: "1.5px solid var(--pgm-magenta)", background: "var(--pgm-cream)", padding: 12, resize: "vertical", boxSizing: "border-box" }}
            data-testid={`promo-edit-textarea-${assetKey}`}
          />
          {saveError && <div style={{ fontSize: 12, color: "var(--pgm-orange)", marginTop: 6 }} data-testid={`save-error-${assetKey}`}>{saveError}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="pgm-btn" style={btnStyle} onClick={save} disabled={saving} data-testid={`save-${assetKey}`}>
              <Check size={12} /> {saving ? "Saving..." : "Save"}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={() => setEditing(false)} disabled={saving} data-testid={`cancel-${assetKey}`}>
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial", fontSize: 13.5, color: "var(--pgm-ink)", lineHeight: 1.55, margin: 0 }}>{text}</pre>
      )}
      {!editing && !(text || "").trim() && (
        <button
          className="pgm-btn pgm-btn-ghost"
          style={{ width: "100%", justifyContent: "center", padding: "14px 12px", fontSize: 12, borderStyle: "dashed", opacity: 0.85 }}
          onClick={startEdit}
          data-testid={`empty-${assetKey}`}
        >
          <Pencil size={12} /> Write your own, or hit Generate
        </button>
      )}

      {historyOpen && !editing && (
        <PromoHistoryPanel
          guestId={guestId}
          assetKey={assetKey}
          entries={history}
          onRestored={onSaved}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {chatOpen && !editing && (
        <PromoAssistantPanel
          guestId={guestId}
          assetKey={assetKey}
          assetLabel={label}
          currentText={text || ""}
          onApplyRevision={onSaved}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  );
}

function RegenConfirmDialog({ onAll, onUnedited, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(43, 16, 48, 0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} data-testid="regen-confirm-dialog">
      <div className="pgm-card" style={{ maxWidth: 500, width: "100%", borderLeftColor: "var(--pgm-orange)" }}>
        <div className="pgm-display" style={{ fontSize: 24, marginBottom: 8 }}>Keep your edits?</div>
        <p style={{ fontSize: 13.5, lineHeight: 1.55, marginBottom: 18, color: "var(--pgm-ink)" }}>
          Some assets have been edited by hand. Regenerate all and lose those edits, or only regenerate unedited assets?
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="pgm-btn" onClick={onUnedited} data-testid="regen-unedited-btn">
            <Sparkles size={13} /> Only unedited
          </button>
          <button className="pgm-btn pgm-btn-ghost" style={{ borderColor: "var(--pgm-orange)", color: "var(--pgm-orange)" }} onClick={onAll} data-testid="regen-all-btn">
            <RefreshCw size={13} /> Regenerate all
          </button>
          <button className="pgm-btn pgm-btn-ghost" onClick={onCancel} data-testid="regen-cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function PromoAssistantPanel({ guestId, assetKey, assetLabel, currentText, onApplyRevision, onClose }) {
  const [messages, setMessages] = useState([]); // [{role, content}]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(null); // latest revised_text
  const [suggestion, setSuggestion] = useState(null); // show_suggestion dict from latest turn
  const [applying, setApplying] = useState(false);
  const [savingShow, setSavingShow] = useState(false);
  const [applied, setApplied] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const nextMsgs = [...messages, { role: "user", content: text }];
    setMessages(nextMsgs);
    setInput(""); setSending(true); setError(""); setApplied(false); setShowSaved(false);
    try {
      const { data } = await api.post(`/guests/${guestId}/promo/${assetKey}/chat`, {
        current_text: revision || currentText,
        messages: nextMsgs,
      });
      setMessages([...nextMsgs, { role: "assistant", content: data.reply || "" }]);
      if (data.revised_text) setRevision(data.revised_text);
      setSuggestion(data.show_suggestion || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "AI chat failed — try again");
      setMessages(nextMsgs.slice(0, -1)); // roll back the user message that failed
      setInput(text);
    } finally { setSending(false); }
  };

  const applyRevision = async () => {
    if (!revision) return;
    setApplying(true); setError("");
    try {
      const { data } = await api.put(`/guests/${guestId}/promo/${assetKey}`, { text: revision });
      onApplyRevision(data);
      setApplied(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to apply revision");
    } finally { setApplying(false); }
  };

  const applyShowSuggestion = async () => {
    if (!suggestion) return;
    setSavingShow(true); setError("");
    try {
      await api.put("/shows/me", suggestion);
      setShowSaved(true);
      setSuggestion(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update show settings");
    } finally { setSavingShow(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); }
  };

  return (
    <div style={{ marginTop: 14, padding: 14, background: "var(--pgm-cream)", borderLeft: "3px solid var(--pgm-magenta)" }} data-testid={`ai-panel-${assetKey}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--pgm-magenta)" }}>
          <MessageCircle size={13} /> AI Assistant &mdash; {assetLabel}
        </div>
        <button className="pgm-btn pgm-btn-ghost" style={{ padding: "4px 8px", fontSize: 11 }} onClick={onClose} data-testid={`ai-close-${assetKey}`}>
          <X size={11} /> Close
        </button>
      </div>

      <div
        ref={scrollRef}
        style={{ maxHeight: 260, overflowY: "auto", background: "#fff", padding: 12, marginBottom: 10, border: "1px solid var(--pgm-rule)" }}
        data-testid={`ai-chat-history-${assetKey}`}
      >
        {messages.length === 0 && !sending && (
          <div style={{ fontSize: 12, opacity: 0.7, fontStyle: "italic" }}>
            Ask for changes in plain English. Examples: &ldquo;Make it punchier&rdquo;, &ldquo;Less corporate&rdquo;, &ldquo;Cut the CTA&rdquo;, &ldquo;Use the specific number from her bio&rdquo;, &ldquo;Never use the word unlock&rdquo;.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ marginBottom: 8, fontSize: 13, lineHeight: 1.5 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: m.role === "user" ? "var(--pgm-wine)" : "var(--pgm-olive)", marginBottom: 2 }}>
              {m.role === "user" ? "You" : "AI"}
            </div>
            <div style={{ whiteSpace: "pre-wrap", color: "var(--pgm-ink)" }}>{m.content}</div>
          </div>
        ))}
        {sending && <div style={{ fontSize: 12, opacity: 0.65 }} data-testid={`ai-thinking-${assetKey}`}>AI is thinking…</div>}
      </div>

      {revision && !sending && (
        <div style={{ marginBottom: 10, padding: 12, background: "#fff", border: "1px dashed var(--pgm-magenta)" }} data-testid={`ai-revision-${assetKey}`}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--pgm-magenta)", marginBottom: 6 }}>Proposed revision</div>
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial", fontSize: 13, lineHeight: 1.55, margin: 0, maxHeight: 280, overflowY: "auto" }}>{revision}</pre>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button className="pgm-btn" style={{ padding: "6px 12px", fontSize: 11 }} onClick={applyRevision} disabled={applying} data-testid={`ai-apply-${assetKey}`}>
              <Check size={12} /> {applying ? "Applying…" : applied ? "Applied ✓" : "Apply this version"}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => setRevision(null)} disabled={applying} data-testid={`ai-discard-${assetKey}`}>
              <X size={12} /> Discard
            </button>
          </div>
        </div>
      )}

      {suggestion && !sending && (
        <div style={{ marginBottom: 10, padding: 12, background: "#fff", border: "1px dashed var(--pgm-olive)" }} data-testid={`ai-suggestion-${assetKey}`}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--pgm-olive)", marginBottom: 6 }}>Also update your show settings?</div>
          <p style={{ fontSize: 12, opacity: 0.75, margin: "0 0 8px 0" }}>The AI noticed a persistent preference. Apply this to your whole show so future promo copy respects it too.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
            {Object.entries(suggestion).map(([k, v]) => (
              <div key={k} style={{ fontSize: 12 }}>
                <strong>{SHOW_FIELD_LABELS[k] || k}:</strong>
                <div style={{ whiteSpace: "pre-wrap", padding: 6, background: "var(--pgm-cream)", marginTop: 3, fontSize: 12, maxHeight: 120, overflowY: "auto" }}>{String(v)}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="pgm-btn pgm-btn-olive" style={{ padding: "6px 12px", fontSize: 11 }} onClick={applyShowSuggestion} disabled={savingShow} data-testid={`ai-apply-show-${assetKey}`}>
              <Check size={12} /> {savingShow ? "Saving…" : showSaved ? "Saved ✓" : "Save to show settings"}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => setSuggestion(null)} disabled={savingShow} data-testid={`ai-dismiss-show-${assetKey}`}>
              <X size={12} /> Not now
            </button>
          </div>
        </div>
      )}

      {applied && !revision && (
        <div style={{ fontSize: 12, color: "var(--pgm-olive)", marginBottom: 8 }}>Revision applied. Ask for more changes or close the chat.</div>
      )}
      {error && <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginBottom: 8 }} data-testid={`ai-error-${assetKey}`}>{error}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          rows={2}
          placeholder="Ask for changes… (Ctrl/Cmd + Enter to send)"
          style={{ flex: 1, fontFamily: "Arial", fontSize: 13, lineHeight: 1.5, color: "var(--pgm-ink)", border: "1.5px solid var(--pgm-magenta)", background: "#fff", padding: 8, resize: "vertical", boxSizing: "border-box" }}
          disabled={sending}
          data-testid={`ai-input-${assetKey}`}
        />
        <button className="pgm-btn" style={{ padding: "6px 14px", fontSize: 12, alignSelf: "flex-end" }} onClick={send} disabled={sending || !input.trim()} data-testid={`ai-send-${assetKey}`}>
          <Send size={12} /> Send
        </button>
      </div>
    </div>
  );
}

function SwipeSplitterCards({ guestId, swipe, isEdited, regenBusy, disabled, onRegen, onSaved }) {
  const segments = splitSwipe(swipe);
  const [chatOpen, setChatOpen] = useState(false);
  const btnStyle = { padding: "6px 12px", fontSize: 11 };
  return (
    <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)" }} data-testid="promo-guest_swipe">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="pgm-micro">◆ Guest social posts — LinkedIn / Facebook / Instagram</div>
          {isEdited && (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", background: "var(--pgm-olive)", color: "var(--pgm-cream)", padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="edited-badge-guest_swipe">
              <Pencil size={9} /> Edited
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="pgm-btn pgm-btn-ghost"
            style={{ ...btnStyle, borderColor: chatOpen ? "var(--pgm-magenta)" : undefined, color: chatOpen ? "var(--pgm-magenta)" : undefined }}
            onClick={() => setChatOpen(v => !v)}
            disabled={regenBusy || disabled}
            data-testid="ai-assist-guest_swipe"
          >
            <Wand2 size={12} /> {chatOpen ? "Close chat" : "AI Assistant"}
          </button>
          <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={onRegen} disabled={regenBusy || disabled} data-testid="regen-guest_swipe">
            <RefreshCw size={12} /> {regenBusy ? "Regenerating..." : "Regenerate all 3"}
          </button>
        </div>
      </div>
      <p style={{ fontSize: 12, opacity: 0.7, margin: "0 0 12px 0" }}>Three different tones for the guest to choose from when sharing your episode.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {segments.map((seg, i) => (
          <SwipeOptionCard
            key={`swipe-${i}-${(seg?.header || seg?.body || "").slice(0, 24)}`}
            index={i}
            segment={seg}
            allSegments={segments}
            guestId={guestId}
            onSaved={onSaved}
            disabled={regenBusy || disabled}
          />
        ))}
      </div>
      {chatOpen && (
        <PromoAssistantPanel
          guestId={guestId}
          assetKey="guest_swipe"
          assetLabel="Guest swipe copy (3 options)"
          currentText={swipe || ""}
          onApplyRevision={onSaved}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  );
}

// Parse the AI-generated guest_email into {subjects: [3 strings], body: string}.
// Expected format:
//   SUBJECT 1 — ...
//   SUBJECT 2 — ...
//   SUBJECT 3 — ...
//   (blank)
//   BODY
//   ...body text...
function parseGuestEmail(raw) {
  if (!raw) return { subjects: [], body: "" };
  const text = raw.replace(/\r\n/g, "\n").trim();
  const subjectRe = /^SUBJECT\s*(\d+)\s*[—\-–:]\s*(.+)$/gim;
  const subjects = [];
  let match;
  while ((match = subjectRe.exec(text)) !== null) {
    subjects.push(match[2].trim());
  }
  // Body starts after a line containing just "BODY" (case-insensitive), or after the last subject line.
  const bodyMarkerIdx = text.search(/^\s*BODY\s*$/im);
  let body = "";
  if (bodyMarkerIdx >= 0) {
    const after = text.slice(bodyMarkerIdx).replace(/^\s*BODY\s*\n?/i, "");
    body = after.trim();
  } else if (subjects.length > 0) {
    // fallback: everything after the last SUBJECT line
    const lastSubjectMatch = [...text.matchAll(subjectRe)];
    if (lastSubjectMatch.length > 0) {
      const last = lastSubjectMatch[lastSubjectMatch.length - 1];
      const idx = last.index + last[0].length;
      body = text.slice(idx).trim();
    }
  } else {
    body = text;
  }
  return { subjects: subjects.slice(0, 3), body };
}

function GuestEmailCard({ guestId, email, isEdited, history = [], regenBusy, disabled, onRegen, onSaved, variant = "guest" }) {
  const parsed = parseGuestEmail(email);
  const [chatOpen, setChatOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [copied, setCopied] = useState("");
  const btnStyle = { padding: "6px 12px", fontSize: 11 };
  const assetKey = variant === "host" ? "host_email_swipe" : "guest_email";
  const cardLabel = variant === "host" ? "Your email swipe — for your own list (host)" : "Guest email swipe — for the guest's list (3 subject lines)";
  const explainer = variant === "host"
    ? "Ready-to-send email you can paste into your own newsletter to hype this episode — three subject-line options to pick from."
    : "Ready-to-send email the guest can paste into their own newsletter — three subject-line options to choose from (curiosity / benefit / plain).";
  const borderColor = variant === "host" ? "var(--pgm-olive)" : "var(--pgm-magenta)";

  const copy = (text, tag) => {
    navigator.clipboard.writeText(text);
    setCopied(tag);
    setTimeout(() => setCopied(""), 1400);
  };

  const startEdit = () => { setDraft(email || ""); setSaveError(""); setEditing(true); };
  const save = async () => {
    setSaving(true); setSaveError("");
    try {
      const { data } = await api.put(`/guests/${guestId}/promo/${assetKey}`, { text: draft });
      onSaved(data);
      setEditing(false);
    } catch (e) {
      setSaveError(e?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  return (
    <div className="pgm-card" style={{ borderLeftColor: borderColor }} data-testid={`promo-${assetKey}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="pgm-micro">◆ {cardLabel}</div>
          {isEdited && (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", background: "var(--pgm-olive)", color: "var(--pgm-cream)", padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`edited-badge-${assetKey}`}>
              <Pencil size={9} /> Edited
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="pgm-btn pgm-btn-ghost"
            style={{ ...btnStyle, borderColor: chatOpen ? "var(--pgm-magenta)" : undefined, color: chatOpen ? "var(--pgm-magenta)" : undefined }}
            onClick={() => setChatOpen(v => !v)}
            disabled={regenBusy || disabled}
            data-testid={`ai-assist-${assetKey}`}
          >
            <Wand2 size={12} /> {chatOpen ? "Close chat" : "AI Assistant"}
          </button>
          <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={onRegen} disabled={regenBusy || disabled} data-testid={`regen-${assetKey}`}>
            <RefreshCw size={12} /> {regenBusy ? "Generating..." : ((email || "").trim() ? "Regenerate" : "Generate")}
          </button>
          {history.length > 0 && (
            <button
              className="pgm-btn pgm-btn-ghost"
              style={{ ...btnStyle, borderColor: historyOpen ? "var(--pgm-olive)" : undefined, color: historyOpen ? "var(--pgm-olive)" : undefined }}
              onClick={() => setHistoryOpen(v => !v)}
              data-testid={`history-${assetKey}`}
            >
              <History size={12} /> History ({history.length})
            </button>
          )}
        </div>
      </div>
      <p style={{ fontSize: 12, opacity: 0.7, margin: "0 0 12px 0" }}>{explainer}</p>

      {editing ? (
        <div style={{ marginTop: 8 }}>
          <textarea
            className="pgm-textarea"
            style={{ minHeight: 260, fontFamily: "Arial", fontSize: 13, lineHeight: 1.55 }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid={`promo-${assetKey}-editor`}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button className="pgm-btn" style={btnStyle} onClick={save} disabled={saving} data-testid={`promo-${assetKey}-save`}>
              <Check size={12} /> {saving ? "Saving…" : "Save"}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={() => { setEditing(false); setSaveError(""); }} data-testid={`promo-${assetKey}-cancel`}>
              Cancel
            </button>
          </div>
          {saveError && <div style={{ marginTop: 8, fontSize: 12, color: "var(--pgm-orange)" }}>{saveError}</div>}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }} data-testid={`promo-${assetKey}-subjects`}>
            {parsed.subjects.length === 0 ? (
              <div style={{ fontSize: 12, opacity: 0.6, fontStyle: "italic" }}>No subject lines yet — click Edit to write your own, or hit Generate.</div>
            ) : parsed.subjects.map((s, i) => (
              <div key={`${assetKey}-subj-${i}-${(s || "").slice(0, 24)}`} style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "var(--pgm-cream)", padding: 10, borderLeft: "2px solid var(--pgm-olive)" }}>
                <div style={{ minWidth: 68, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--pgm-magenta)", paddingTop: 3 }}>
                  Subject {i + 1}
                </div>
                <div style={{ flex: 1, fontSize: 13.5, color: "var(--pgm-ink)" }} data-testid={`promo-${assetKey}-subject-${i}`}>{s}</div>
                <button
                  className="pgm-btn pgm-btn-ghost"
                  style={{ padding: "3px 10px", fontSize: 10 }}
                  onClick={() => copy(s, `s${i}`)}
                  data-testid={`copy-${assetKey}-subject-${i}`}
                >
                  <Copy size={10} /> {copied === `s${i}` ? "Copied ✓" : "Copy"}
                </button>
              </div>
            ))}
          </div>
          <div style={{ background: "#fff", border: "1px solid var(--pgm-rule)", padding: 12 }} data-testid={`promo-${assetKey}-body`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--pgm-magenta)" }}>Body</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  className="pgm-btn pgm-btn-ghost"
                  style={{ padding: "3px 10px", fontSize: 10 }}
                  onClick={() => copy(parsed.body, "body")}
                  data-testid={`copy-${assetKey}-body`}
                >
                  <Copy size={10} /> {copied === "body" ? "Copied ✓" : "Copy body"}
                </button>
                <button
                  className="pgm-btn pgm-btn-ghost"
                  style={{ padding: "3px 10px", fontSize: 10 }}
                  onClick={() => copy(email || "", "all")}
                  data-testid={`copy-${assetKey}-all`}
                >
                  <Copy size={10} /> {copied === "all" ? "Copied ✓" : "Copy all"}
                </button>
                <button
                  className="pgm-btn pgm-btn-ghost"
                  style={{ padding: "3px 10px", fontSize: 10 }}
                  onClick={startEdit}
                  disabled={regenBusy || disabled}
                  data-testid={`edit-${assetKey}`}
                >
                  <Pencil size={10} /> Edit
                </button>
              </div>
            </div>
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial", fontSize: 13.5, lineHeight: 1.6, color: "var(--pgm-ink)", margin: 0 }}>{parsed.body}</pre>
          </div>
        </>
      )}

      {historyOpen && !editing && (
        <PromoHistoryPanel
          guestId={guestId}
          assetKey={assetKey}
          entries={history}
          onRestored={onSaved}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {chatOpen && (
        <PromoAssistantPanel
          guestId={guestId}
          assetKey={assetKey}
          assetLabel={cardLabel}
          currentText={email || ""}
          onApplyRevision={onSaved}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  );
}

function SwipeOptionCard({ index, segment, allSegments, guestId, onSaved, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saveError, setSaveError] = useState("");

  const heading = segment.header || `Option ${index + 1}`;
  const startEdit = () => { setDraft(segment.body); setSaveError(""); setEditing(true); };
  const save = async () => {
    setSaving(true); setSaveError("");
    try {
      const next = allSegments.map((s, i) => (i === index ? { ...s, body: draft.trim() } : s));
      const joined = joinSwipe(next);
      const { data } = await api.put(`/guests/${guestId}/promo/guest_swipe`, { text: joined });
      onSaved(data);
      setEditing(false);
    } catch (e) {
      setSaveError(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };
  const copy = () => {
    navigator.clipboard.writeText(segment.body || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const btnStyle = { padding: "5px 10px", fontSize: 11 };

  return (
    <div style={{ background: "var(--pgm-cream)", padding: 14, borderLeft: "2px solid var(--pgm-olive)" }} data-testid={`swipe-option-${index + 1}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--pgm-wine)" }}>{heading}</div>
        {!editing && (
          <div style={{ display: "flex", gap: 6 }}>
            <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={copy} data-testid={`copy-swipe-option-${index + 1}`}>
              {copied ? <><Check size={11} /> Copied!</> : <><Copy size={11} /> Copy</>}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={startEdit} disabled={disabled} data-testid={`edit-swipe-option-${index + 1}`}>
              <Pencil size={11} /> Edit
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.min(14, Math.max(3, draft.split("\n").length + 1))}
            style={{ width: "100%", fontFamily: "Arial", fontSize: 13.5, lineHeight: 1.55, color: "var(--pgm-ink)", border: "1.5px solid var(--pgm-magenta)", background: "#fff", padding: 10, resize: "vertical", boxSizing: "border-box" }}
            data-testid={`swipe-edit-textarea-${index + 1}`}
          />
          {saveError && <div style={{ fontSize: 12, color: "var(--pgm-orange)", marginTop: 6 }}>{saveError}</div>}
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button className="pgm-btn" style={btnStyle} onClick={save} disabled={saving} data-testid={`save-swipe-option-${index + 1}`}>
              <Check size={11} /> {saving ? "Saving..." : "Save"}
            </button>
            <button className="pgm-btn pgm-btn-ghost" style={btnStyle} onClick={() => setEditing(false)} disabled={saving} data-testid={`cancel-swipe-option-${index + 1}`}>
              <X size={11} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial", fontSize: 13.5, color: "var(--pgm-ink)", lineHeight: 1.55, margin: 0 }}>{segment.body}</pre>
      )}
    </div>
  );
}

function ShareKit({ g, onKitImages }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const images = g.kit_images || [];
  const kitUrl = `${window.location.origin}/promo-kit/${g.intake_token}`;

  const copyLink = () => {
    navigator.clipboard.writeText(kitUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setErr("");
    try {
      let latest = images;
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post(`/guests/${g.id}/kit-images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        latest = data.kit_images;
      }
      onKitImages(latest);
    } catch (error) {
      setErr(error?.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = async (url) => {
    const { data } = await api.delete(`/guests/${g.id}/kit-images`, { params: { url } });
    onKitImages(data.kit_images);
  };

  return (
    <div style={{ marginTop: 28 }} data-testid="share-kit-section">
      <div className="pgm-divider"><strong>◆ GUEST SHARE KIT</strong></div>
      <div className="pgm-callout" style={{ marginBottom: 14 }}>
        <strong>How this works:</strong> The guest gets a personal promo kit page with their episode details, ready-to-post copy (from the promo above), and any graphics you upload below. The <code>{"{promo_kit_url}"}</code> placeholder in your emails links here — it's included in the pre-show check-in by default.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <button className="pgm-btn pgm-btn-ghost" onClick={copyLink} data-testid="copy-kit-link">
          <Link2 size={14} /> {copied ? "Copied!" : "Copy kit link"}
        </button>
        <a href={kitUrl} target="_blank" rel="noreferrer" className="pgm-btn pgm-btn-ghost" style={{ textDecoration: "none" }} data-testid="preview-kit-link">
          <ExternalLink size={14} /> Preview kit page
        </a>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPick} style={{ display: "none" }} data-testid="kit-image-input" />
        <button className="pgm-btn" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="kit-image-upload-btn">
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload images"}
        </button>
      </div>
      {err && <div style={{ color: "var(--pgm-orange)", fontSize: 12, marginBottom: 10 }} data-testid="kit-upload-error">{err}</div>}
      {images.length === 0 ? (
        <div style={{ fontSize: 13, opacity: 0.65 }} data-testid="kit-images-empty">No graphics uploaded yet — add episode graphics, quote cards, or promo tiles for the guest to share.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }} data-testid="kit-images-grid">
          {images.map((img, i) => (
            <div key={img.url} className="pgm-card" style={{ padding: 8 }} data-testid={`kit-admin-image-${i}`}>
              <img src={img.url.startsWith("/api") ? `${BACKEND_URL}${img.url}` : img.url} alt={img.name} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }} />
              <button className="pgm-btn pgm-btn-ghost" style={{ width: "100%", justifyContent: "center", marginTop: 8, padding: "6px 10px", fontSize: 11, borderColor: "var(--pgm-orange)", color: "var(--pgm-orange)" }} onClick={() => removeImage(img.url)} data-testid={`kit-admin-image-remove-${i}`}>
                <Trash2 size={12} /> Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {(g.guest_uploads || []).length > 0 && (
        <div style={{ marginTop: 22 }} data-testid="guest-uploads-section">
          <div className="pgm-divider" style={{ margin: "8px 0 14px" }}><strong>◆ IMAGES THE GUEST UPLOADED</strong></div>
          <p style={{ fontSize: 12.5, opacity: 0.7, margin: "0 0 12px 0" }}>Headshots, quote cards, or extra promo images the guest added on their kit page. Grab any you'd like to use in your own marketing.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }} data-testid="guest-uploads-grid">
            {g.guest_uploads.map((img, i) => (
              <div key={img.url} className="pgm-card" style={{ padding: 8 }} data-testid={`guest-upload-image-${i}`}>
                <img src={img.url.startsWith("/api") ? `${BACKEND_URL}${img.url}` : img.url} alt={img.name || "guest upload"} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }} />
                <a
                  href={img.url.startsWith("/api") ? `${BACKEND_URL}${img.url}` : img.url}
                  download={img.name || "guest-upload"}
                  className="pgm-btn pgm-btn-ghost"
                  style={{ width: "100%", justifyContent: "center", marginTop: 8, padding: "6px 10px", fontSize: 11, textDecoration: "none", display: "flex", alignItems: "center", gap: 6 }}
                  data-testid={`guest-upload-image-download-${i}`}
                >
                  <Download size={12} /> Download
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PromoTab;
